import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SafeImage } from '@/components/common/safe-image';
import useMessageInfo from '@/components/conversations/conversation-list/message-list-item/use-message-info';
import useJoinGroup from '@/hooks/groups/use-join-group';
import { inviteMessageScheme } from '@/models/messages/message-type';
import { makePrompt } from '@/stores/prompter';
import { addToast } from '@/stores/toast';
import type { ContentCommonProps } from './content-common-props';

export default function ContentInvite({ message }: ContentCommonProps) {
  const { t } = useTranslation();
  const { isMine } = useMessageInfo(message);

  const inviteContent = useMemo(() => {
    try {
      const { success, data } = inviteMessageScheme.safeParse(JSON.parse(message.Message));

      return success ? data : undefined;
    } catch (e) {
      console.error(e);
      return undefined;
    }
  }, [message.Message]);

  const { mutateAsync: joinGroup } = useJoinGroup(inviteContent?.JoinType);

  const handleClick = async () => {
    if (isMine || !inviteContent) return;

    await makePrompt({
      key: 'join-group',
      type: 'dialog',
      title: t('conversation.group.join-confirm'),
      content: t('conversation.group.join-confirm'),
      promise: async () => {
        const response = await joinGroup({ groupId: inviteContent.GroupID, message });

        if (response.Code === 0) {
          addToast({
            type: 'success',
            message: response.Msg,
          });
        }
      },
    });
  };

  if (!inviteContent) return null;

  return (
    <div
      className='chat-bubble min-w-min before:hidden group-data-receiver:chat-bubble-plain group-data-sender:chat-bubble-primary'
      onClick={handleClick}>
      <div className='flex flex-col items-center justify-center gap-1'>
        <div className='flex min-w-24 flex-col items-center justify-center gap-2 rounded-md p-2 group-data-sender:theme-ryuryu:bg-blue-300 group-data-sender:theme-wangyou:bg-teal-200 group-data-sender:theme-threetwoone:bg-purple-100 group-data-sender:theme-eightk:bg-purple-100 group-data-sender:theme-bonglan:bg-red-300 group-data-sender:theme-kbar:bg-red-300 group-data-sender:text-body bg-base-200 text-body'>
          <div className='avatar'>
            <div className='w-10 rounded-full'>
              <SafeImage
                className='size-10 bg-cover'
                src={inviteContent.GroupImg}
              />
            </div>
          </div>

          <div>
            <span className='line-clamp-2 break-all'>{`${inviteContent.GroupName} (${inviteContent.UserCount})`}</span>
          </div>
        </div>

        <span className='text-xs text-body'>
          {isMine ? t('conversation.group.invite-friend') : t('conversation.group.invite-you')}
        </span>
        {!isMine && <span className='text-xs opacity-70 text-body underline'>{t('conversation.click-to-join')}</span>}
      </div>
    </div>
  );
}
